import React from 'react';
import { ArrowLeft, Building2, Home, Factory, Calendar, User, DollarSign, FileText } from 'lucide-react';
import { useProjects } from '../contexts/ProjectsContext';
import { useClients } from '../contexts/ClientsContext';

interface ProjectDetailProps {
  projectId: string;
  onBack: () => void;
}

function ProjectDetail({ projectId, onBack }: ProjectDetailProps) {
  const { projects } = useProjects();
  const { clients } = useClients();

  const project = projects.find(p => p.id === projectId);

  if (!project) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-main">
          <div className="empty-state-large">
            <p className="empty-text">Project not found</p>
            <button className="btn-primary" onClick={onBack}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const client = clients.find(c => c.id === project.clientId);
  const projectTypeIcons: { [key: string]: any } = {
    residential: Home,
    commercial: Building2,
    industrial: Factory,
  };
  const Icon = projectTypeIcons[project.projectType] || Building2;

  return (
    <div className="dashboard-container">
      <div className="dashboard-main">
        <button className="back-button" onClick={onBack}>
          <ArrowLeft size={18} />
          Back to All Projects
        </button>

        <div className="project-detail-header">
          <div className="project-detail-icon">
            <Icon size={32} />
          </div>
          <div>
            <h1 className="dashboard-page-title" style={{ marginBottom: '0.5rem' }}>{project.name}</h1>
            <div className={`project-status-badge-large status-${project.status}`}>
              {project.status === 'in-progress' ? 'Active' :
               project.status.charAt(0).toUpperCase() + project.status.slice(1)}
            </div>
          </div>
        </div>

        <div className="project-detail-grid">
          <div className="project-detail-card">
            <h3 className="detail-card-title">Project Information</h3>
            <div className="detail-list">
              <div className="detail-item-large">
                <User size={18} className="detail-icon" />
                <div>
                  <div className="detail-label">Client</div>
                  <div className="detail-value">{client?.name || 'Unknown'}</div>
                </div>
              </div>
              {project.bidDeadline && (
                <div className="detail-item-large">
                  <Calendar size={18} className="detail-icon" />
                  <div>
                    <div className="detail-label">Bid Deadline</div>
                    <div className="detail-value">
                      {new Date(project.bidDeadline).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                  </div>
                </div>
              )}
              {project.estimatedBudget && (
                <div className="detail-item-large">
                  <DollarSign size={18} className="detail-icon" />
                  <div>
                    <div className="detail-label">Estimated Budget</div>
                    <div className="detail-value">${project.estimatedBudget.toLocaleString()}</div>
                  </div>
                </div>
              )}
               {project.files && project.files.length > 0 && (
                 <div className="detail-item-large">
                   <FileText size={18} className="detail-icon" />
                   <div>
                     <div className="detail-label">Files ({project.files.length})</div>
                     <div className="detail-value">
                       {project.files.map((file, idx) => (
                         <div key={file.id}>
                           {file.name}
                           {idx < project.files!.length - 1 && ', '}
                         </div>
                       ))}
                     </div>
                   </div>
                 </div>
               )}
            </div>
          </div>

          {project.description && (
            <div className="project-detail-card">
              <h3 className="detail-card-title">Description</h3>
              <p className="project-description">{project.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProjectDetail;

